import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MdDashboard, MdPeople, MdPayments, MdEmojiEvents,
  MdCampaign, MdBarChart, MdFitnessCenter, MdClose,
  MdCheckCircle, MdSchedule, MdSettings, MdPerson,
  MdStar, MdCalendarMonth, MdAnnouncement, MdMilitaryTech,
  MdLeaderboard, MdHourglassTop, MdCreditCard, MdQrCode, MdShield,
  MdPersonAdd, MdStorefront, MdRocketLaunch, MdReceiptLong, MdSecurity,
  MdHistory, MdDescription, MdNotes, MdEventAvailable,
} from 'react-icons/md';
import { FaUserTie } from 'react-icons/fa';
import BrandLogo from '../common/BrandLogo';
import useAuth from '../../hooks/useAuth';
import useTheme from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import { vocab, hasBelts, hasClasses } from '../../utils/publicProfile';
import { getPendingCount } from '../../services/approvalsService';
import { getMySubscription } from '../../services/subscriptionService';
import { getCommandCenter } from '../../services/adminService';
import { TRIAL_ENABLED, APPROVALS_ENABLED } from '../../config/features';

const menuByRole = {
  admin: [
    {
      to: '/admin/dashboard', Icon: MdDashboard, label: 'Dashboard',
      // Stay active for dashboard-origin shortcuts (Website Requests has no admin sidebar item)
      activeWhen: (loc) =>
        loc.pathname === '/admin/dashboard' ||
        (['/admin/trial-students', '/admin/website-requests'].includes(loc.pathname) &&
         new URLSearchParams(loc.search).get('source') === 'public_website'),
    },
    { to: '/admin/students',       Icon: MdPeople,       label: 'Students', badge: 'website-leads' },
    { to: '/admin/trainers',       Icon: FaUserTie,      label: 'Trainers'        },
    { to: '/admin/admins',         Icon: MdShield,       label: 'Admins', primaryOnly: true },
    { to: '/admin/classes',        Icon: MdFitnessCenter,label: 'Classes',        classOnly: true },
    { to: '/admin/attendance',     Icon: MdEventAvailable, label: 'Attendance',    classOnly: true },
    { to: '/admin/payments',       Icon: MdPayments,     label: 'Payments'        },
    { to: '/admin/belt-exams',     Icon: MdEmojiEvents,  label: 'Belt Exams',     beltOnly: true },
    { to: '/admin/grading',        Icon: MdCheckCircle,  label: 'Grading',        beltOnly: true },
    { to: '/admin/announcements',  Icon: MdCampaign,     label: 'Announcements'   },
    { to: '/admin/student-records',Icon: MdDescription,  label: 'Student Records' },
    { to: '/admin/training-journal',Icon: MdNotes,       label: 'Training Journal', classOnly: true },
    { to: '/admin/reports',        Icon: MdBarChart,     label: 'Reports'         },
    { to: '/admin/finance',        Icon: MdReceiptLong,  label: 'Finance'         },
    { to: '/admin/marketing',      Icon: MdRocketLaunch, label: 'Marketing'       },
    { to: '/admin/club-profile',   Icon: MdStorefront,   label: 'Club Profile'    },
    ...(APPROVALS_ENABLED ? [{ to: '/admin/approvals', Icon: MdHourglassTop, label: 'Approvals', badge: 'pending' }] : []),
    { to: '/admin/subscription',   Icon: MdCreditCard,   label: 'Subscription',   badge: 'upgrade' },
    { to: '/admin/settings',        Icon: MdSettings,     label: 'Settings'        },
    { to: '/admin/support-access',  Icon: MdSecurity,     label: 'Support Access'  },
    { to: '/admin/activity',        Icon: MdHistory,      label: 'Activity Log'    },
  ],
  trainer: [
    { to: '/trainer/dashboard',            Icon: MdDashboard,    label: 'Dashboard'       },
    { to: '/trainer/students',             Icon: MdPeople,        label: 'My Students'     },
    { to: '/trainer/attendance',           Icon: MdEventAvailable, label: 'Attendance',    classOnly: true },
    { to: '/trainer/schedule',             Icon: MdSchedule,      label: 'My Schedule',    classOnly: true },
    { to: '/trainer/belt-recommendations', Icon: MdEmojiEvents,   label: 'Belt Recs',      beltOnly: true },
    { to: '/trainer/belt-exams',          Icon: MdStar,          label: 'Belt Exams',     beltOnly: true },
    ...(TRIAL_ENABLED ? [{ to: '/trainer/trial-students', Icon: MdPersonAdd, label: 'Website Requests' }] : []),
    { to: '/trainer/announcements',        Icon: MdAnnouncement,  label: 'Announcements'   },
    { to: '/trainer/student-records',      Icon: MdDescription,   label: 'Student Records' },
    { to: '/trainer/training-journal',     Icon: MdNotes,         label: 'Training Journal', classOnly: true },
    { to: '/trainer/expenses',             Icon: MdReceiptLong,   label: 'Club Expenses'   },
    { to: '/trainer/settings',             Icon: MdSettings,      label: 'Settings'        },
  ],
  student: [
    { to: '/student/dashboard',       Icon: MdDashboard,    label: 'Dashboard'      },
    { to: '/student/profile',         Icon: MdPerson,        label: 'My Profile'     },
    { to: '/student/attendance',      Icon: MdCheckCircle,   label: 'Attendance',     classOnly: true },
    { to: '/student/schedule',        Icon: MdCalendarMonth, label: 'Class Schedule', classOnly: true },
    { to: '/student/belt-progress',   Icon: MdStar,          label: 'Belt Progress',  beltOnly: true },
    { to: '/student/fees',            Icon: MdPayments,      label: 'Fee Payment'     },
    { to: '/student/notices',         Icon: MdCampaign,      label: 'Notices'         },
    { to: '/student/training-journal', Icon: MdNotes,         label: 'Training Journal', classOnly: true },
    { to: '/student/leaderboard',     Icon: MdLeaderboard,   label: 'Leaderboard'     },
    { to: '/student/badges',          Icon: MdMilitaryTech,  label: 'My Badges'       },
    { to: '/student/settings',        Icon: MdSettings,      label: 'Settings'        },
  ],
};

function Sidebar({ isOpen = false, onClose = () => {} }) {
  const { user } = useAuth();
  const location = useLocation();
  const { currentTheme } = useTheme();
  const { club, loading: tenantLoading } = useTenant();
  // Belt world is gated by club type (Stage 2). Shown during load so nothing flickers.
  const showBelts = tenantLoading || hasBelts(club?.clubType);
  // Class/session/attendance/training-journal world — hidden for open-floor gym clubs.
  const showClasses = tenantLoading || hasClasses(club?.clubType);
  // Staff Admins (positively known) don't see admin-management items. When the
  // primary flag is unknown (stale session), default to showing — the backend
  // still enforces every admin-management action.
  const isStaffAdmin = user?.role === 'admin' && (user?.isPrimaryAdmin === false || user?.adminType === 'staff');
  const menu = (menuByRole[user?.role] || []).filter(item =>
    (!item.beltOnly || showBelts) &&
    (!item.classOnly || showClasses) &&
    (!item.primaryOnly || !isStaffAdmin)
  );

  // Vertical wording — "Students"→"Members" for a gym, "Trainers"→"Instructors" for yoga, etc.
  // Byte-identical for taekwondo clubs (the vocab words equal the current labels).
  const v = vocab(club?.clubType);
  const labelFor = (label) => label
    .replace(/\bStudents\b/g, v.members)
    .replace(/\bStudent\b/g, v.member)
    .replace(/\bTrainers\b/g, v.trainers)
    .replace(/\bTrainer\b/g, v.trainer);

  const { data: approvalData } = useQuery({
    queryKey: ['approval-count'],
    queryFn:  () => getPendingCount().then(r => r.data),
    enabled:  user?.role === 'admin',
    refetchInterval: 60000,
  });
  const pendingCount = approvalData?.count || 0;

  const { data: ccData } = useQuery({
    queryKey: ['command-center'],
    queryFn:  () => getCommandCenter().then(r => r.data),
    enabled:  user?.role === 'admin',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const websiteLeadsBadge = ccData?.websiteLeads?.pendingCount || 0;

  const { data: subData } = useQuery({
    queryKey: ['my-subscription'],
    queryFn:  () => getMySubscription().then(r => r.data),
    enabled:  user?.role === 'admin',
    staleTime: 5 * 60 * 1000,
  });

  const isProClub = (() => {
    const sub = subData?.subscription;
    if (!sub) return null;
    const plan   = String(sub.plan   || '').toLowerCase().trim();
    const status = String(sub.status || '').toLowerCase().trim();
    return (plan === 'pro' || plan.startsWith('pro')) &&
           (!status || ['active', 'trialing', 'trial'].includes(status));
  })();

  const subBadge = user?.role === 'admin'
    ? (isProClub === null ? null : isProClub ? 'pro' : 'upgrade')
    : null;

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-sidebar flex flex-col
        shadow-2xl transform transition-transform duration-300 ease-in-out
        lg:sticky lg:top-0 lg:h-screen lg:z-auto lg:shadow-none
        lg:border-r lg:border-divider lg:translate-x-0 flex-shrink-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      {/* Logo header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
        <div className="flex items-center gap-2.5">
          <BrandLogo size={40} />
          <div>
            <span className="font-bold text-secondary text-base block leading-tight">TrainAxis</span>
            <span className="text-[10px] text-subtle font-medium tracking-wide">Fitness Management</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover transition-colors"
        >
          <MdClose className="text-xl" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        <p className="text-[11px] font-medium text-subtle uppercase tracking-[0.12em] px-3 mb-2">
          Main Menu
        </p>
        <div className="space-y-0.5">
          {menu.map(({ to, Icon, label, badge, divider, activeWhen }) => (
            <div key={to}>
              {divider && (
                <div className="pt-2 pb-1 mt-1 border-t border-divider">
                  <p className="text-[11px] font-medium text-subtle uppercase tracking-[0.12em] px-3">
                    Internal
                  </p>
                </div>
              )}
              <NavLink
                to={to}
                onClick={onClose}
                className={({ isActive }) => {
                  const active = activeWhen ? activeWhen(location) : isActive;
                  return `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200
                  ${active
                    ? (currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card')
                    : 'text-nav-text hover:bg-nav-hover hover:text-secondary'}`;
                }}
              >
                <Icon className="text-[18px] flex-shrink-0" />
                <span className="flex-1">{labelFor(label)}</span>

                {badge === 'pending' && pendingCount > 0 && (
                  <span className="w-5 h-5 bg-yellow-400 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                    {pendingCount > 9 ? '9+' : pendingCount}
                  </span>
                )}

                {badge === 'website-leads' && websiteLeadsBadge > 0 && (
                  <span className="w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
                    {websiteLeadsBadge > 9 ? '9+' : websiteLeadsBadge}
                  </span>
                )}

                {badge === 'upgrade' && subBadge === 'pro' && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0 uppercase tracking-wide">
                    Pro
                  </span>
                )}
                {badge === 'upgrade' && subBadge === 'upgrade' && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 flex-shrink-0 uppercase tracking-wide">
                    Upgrade
                  </span>
                )}
              </NavLink>
            </div>
          ))}
        </div>
      </nav>
    </aside>
  );
}

export default Sidebar;
